import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { CartOverviewModalConfig as CartOverviewModalContentConfig } from '@front-platform/content-builder';
import { cartOverviewModalResolveConfig } from '@front-platform/content-builder';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { map, take, takeUntil } from 'rxjs/operators';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Component, OnDestroy, OnInit, input } from '@angular/core';
import { combineLatest, from, Observable, Subject, timer } from 'rxjs';

import { Paths } from '@config/paths.config';
import { ShopStoreRefiner } from '@common/refiners/shop.refiner';
import { DistributionModeValues } from '@config/values/order.values';

import { OrderKeys } from '@config/keys/order.keys';
import { DistributionModeKeys, MarketplaceKeys } from '@config/keys/shop.keys';

import { CartStoreRefiner } from '@common/refiners/cart.refiner';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';

import { NativeService } from '@core/services/native/native.service';
import { HeaderService } from '@core/services/header/header.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { RouterHelperService } from 'src/app/core/services/router-helper/router-helper.service';

import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { FooterStickyModalDirective } from '@core/directives/footer-sticky-modal.directive';
import { CartParametersOverviewComponent } from '@shared/components/cart-parameters-overview/cart-parameters-overview.component';
import { CartFooterButtonAtomComponent } from '@shared/atoms/cart-footer-button-atom/cart-footer-button-atom.component';
import { PriceAtomComponent } from '@shared/atoms/price-atom/price-atom.component';
import { CartUtils } from '@app/utilities/cart.util';
import { GetModalInterface, ModalConfig } from '@core/services/modals/modals-util.type';
import {
  CartParametersModalComponent,
  CartParametersModalConfig,
} from '@shared/modals/cart-parameters-modal/cart-parameters-modal.component';

export type CartOverviewModalInputs = {
  goToCartButtonText?: string;
  backgroundColor?: IContentBuilderFieldColor;
  labelColor?: IContentBuilderFieldColor;
  iconColor?: IContentBuilderFieldColor;
  iconBackgroundColor?: IContentBuilderFieldColor;
  buttonBackgroundColor?: IContentBuilderFieldColor;
  buttonTextColor?: IContentBuilderFieldColor;
};

export type CartOverviewModalConfig = ModalConfig<{
  handle: 'cart-overview-modal';
  inputs: CartOverviewModalInputs;
}>;
export interface CartOverviewModalRuntimeConfig {
  'go_to_cart_button_text'?: NonNullable<CartOverviewModalInputs['goToCartButtonText']>;
}

export type CartOverviewModalConfigInput = Partial<
  Omit<CartOverviewModalContentConfig, keyof CartOverviewModalRuntimeConfig> &
    CartOverviewModalRuntimeConfig
>;
export type CartOverviewModalResolvedConfig = Omit<
  CartOverviewModalContentConfig,
  keyof CartOverviewModalRuntimeConfig
> &
  Partial<CartOverviewModalRuntimeConfig>;
export interface CartOverviewModalContract extends ConfigComponentContract<
  CartOverviewModalConfigInput,
  CartOverviewModalResolvedConfig
> {}

@Component({
  selector: 'app-cart-overview-modal',
  templateUrl: './cart-overview-modal.component.html',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    ContentBuilderColorPipe,
    FooterStickyModalDirective,
    CartParametersOverviewComponent,
    CartFooterButtonAtomComponent,
    PriceAtomComponent,
  ],
})
export class CartOverviewModalComponent
  implements
    OnInit,
    OnDestroy,
    GetModalInterface<CartOverviewModalConfig>,
    CartOverviewModalContract
{
  readonly config = input<CartOverviewModalConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    CartOverviewModalContentConfig,
    CartOverviewModalRuntimeConfig
  >(cartOverviewModalResolveConfig, () => ({ config: this.config() }));
  static handle = 'cart-overview-modal' as const;

  get goToCartButtonText(): NonNullable<CartOverviewModalInputs['goToCartButtonText']> {
    return this.resolvedConfig()['go_to_cart_button_text'] as unknown as NonNullable<
      CartOverviewModalInputs['goToCartButtonText']
    >;
  }
  get backgroundColor(): NonNullable<CartOverviewModalInputs['backgroundColor']> {
    return this.resolvedConfig()['background_color'] as unknown as NonNullable<
      CartOverviewModalInputs['backgroundColor']
    >;
  }
  get labelColor(): NonNullable<CartOverviewModalInputs['labelColor']> {
    return this.resolvedConfig()['label_color'] as unknown as NonNullable<
      CartOverviewModalInputs['labelColor']
    >;
  }
  get iconColor(): NonNullable<CartOverviewModalInputs['iconColor']> {
    return this.resolvedConfig()['icon_color'] as unknown as NonNullable<
      CartOverviewModalInputs['iconColor']
    >;
  }
  get iconBackgroundColor(): NonNullable<CartOverviewModalInputs['iconBackgroundColor']> {
    return this.resolvedConfig()['icon_background_color'] as unknown as NonNullable<
      CartOverviewModalInputs['iconBackgroundColor']
    >;
  }
  get buttonBackgroundColor(): NonNullable<CartOverviewModalInputs['buttonBackgroundColor']> {
    return this.resolvedConfig()['button_background_color'] as unknown as NonNullable<
      CartOverviewModalInputs['buttonBackgroundColor']
    >;
  }
  get buttonTextColor(): NonNullable<CartOverviewModalInputs['buttonTextColor']> {
    return this.resolvedConfig()['button_text_color'] as unknown as NonNullable<
      CartOverviewModalInputs['buttonTextColor']
    >;
  }

  private readonly destroyed$ = new Subject<boolean>();

  cart$ = this.cartSelector.selectActive$;
  order$ = this.orderSelector.selectValidation$;
  cartProducts$ = this.cartRefiner.selectActiveCartProducts;
  /** Total units (sum of line quantities) for basket label, not distinct line count */
  cartLineItemsTotalQuantity$ = this.cartRefiner.selectActiveCartProducts.pipe(
    map(products => CartUtils.sumActiveCartLineQuantities(products)),
  );

  isNotched = false;

  orderKeys = OrderKeys;
  isNavigationToCartInProgress = false;

  hideCartParameters$: Observable<boolean> = combineLatest([
    this.shopRefiner.selectActiveShopEnabledDistributionMode,
    this.marketplaceSelector.selectMarketplace$,
  ]).pipe(
    map(([shopDistributionModes, marketplace]) => {
      const isMutiShop = marketplace[MarketplaceKeys.CartScope] === 'MARKETPLACE';

      const distributionModes = isMutiShop
        ? marketplace[MarketplaceKeys.DistributionModes]?.filter(
            mode => mode?.[DistributionModeKeys.Enabled],
          )
        : shopDistributionModes;

      return (
        distributionModes?.length === 1 &&
        distributionModes[0][DistributionModeKeys.Type] === DistributionModeValues.EatIn
      );
    }),
  );

  constructor(
    private readonly router: Router,
    private cartRefiner: CartStoreRefiner,
    private shopRefiner: ShopStoreRefiner,
    private cartSelector: CartStoreSelector,
    private orderSelector: OrderStoreSelector,
    private modalSelector: ModalStoreSelector,
    private readonly modalsService: ModalsService<
      [CartOverviewModalConfig, CartParametersModalConfig]
    >,
    private readonly headerService: HeaderService,
    private settingsSelector: SettingsStoreSelector,
    private readonly routerHelper: RouterHelperService,
    private readonly translateService: TranslateService,
    private marketplaceSelector: MarketplaceStoreSelector,
    private readonly analyticsService: AnalyticsService,
  ) {}

  ngOnInit(): void {
    // TODO: Replace with constant
    this.modalSelector
      .selectModal('authentication-modal')
      .pipe(
        takeUntil(this.destroyed$),
        map(modal => {
          this.isNavigationToCartInProgress = !!modal;
        }),
      )
      .subscribe();

    timer(0).subscribe(() => {
      this.headerService.toggleCartParametersVisibility(true);
      this.headerService.toggleCartButtonVisibility(true);
    });

    from(NativeService.isNotched())
      .pipe(take(1))
      .subscribe(_isNotched => {
        this.isNotched = _isNotched;
      });

    const cart = this.cartSelector.active;
    const parameters = this.settingsSelector.parameters;

    if (
      cart &&
      parameters &&
      parameters.is_distribution_mode_defined &&
      cart.type !== parameters.distribution_mode
    ) {
      this.modalsService.open(CartParametersModalComponent.handle);
      this.modalsService.setData(CartParametersModalComponent.handle, {
        existingCartMessage: this.translateService.instant('parameters.warning-existing-cart'),
      });
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.headerService.toggleCartParametersVisibility(false);
    this.headerService.toggleCartButtonVisibility(false);
  }

  navigateToCart(): void {
    this.isNavigationToCartInProgress = true;
    this.router.navigate([this.routerHelper.translateRoute(Paths.Cart)]);
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(CartOverviewModalComponent, { 'keyAliases': {} });
